import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'redux/store'
import { of, interval, throttle } from 'rxjs'
import { Scroll } from 'libraries/front-end-core/services/WindowService'
import useObservable from 'libraries/front-end-core/hooks/useObservable'
import { RESPONSE_NOTFOUND, RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { requestReferenceNodes } from 'services/LegislationReferenceService'
import selectFlavour from 'redux/selectors/selectFlavour'
import { pageToggleRight, setMouseabilityReference } from 'redux/actions'
import { isInView } from 'helpers/dom'
import { Flavour } from 'types/Flavour'
import TealToolTip from 'components/TealToolTip'
import SpanButton from 'components/SpanButton'
import { updateHistoryState } from 'libraries/front-end-core/core/history'

const getReferences = (element: HTMLSpanElement): string => {
  return Array
    .from(element.getElementsByClassName('fc-reference'))
    .map((_element: Element) => (_element.textContent?.trim() ?? ''))
    .join(',')
    .replace(/[^A-Z0-9:+(),\s._]/gi, '')
}

export default function SmartFcLegislationReference ({ children }: { children: ReactNode }): JSX.Element | null {
  const scroll = useObservable(
    Scroll.pipe(throttle(() => interval(200))), {}
  )
  const [viewed, setViewed] = useState(false)

  const [trigger, setTrigger] = useState(0)
  const dispatch = useDispatch<AppDispatch>()
  const flavour = useSelector(selectFlavour)
  const [references, setReferences] = useState<string>()
  const ref = useRef<HTMLSpanElement>(null)
  const observable = useMemo(
    () => references !== undefined && references.length > 0 && viewed
      ? requestReferenceNodes({ references })
      : of([]),
    [references?.length, references, flavour, viewed, trigger]
  )
  const response: { references: unknown[] | number, flavour: Flavour } = useObservable(
    observable,
    RESPONSE_PENDING,
    [observable]
  )

  const [isArchived, setIsArchived] = useState(false)
  const [isRemoved, setIsRemoved] = useState(false)

  useEffect(() => {
    if (ref.current !== null) {
      setReferences(
        getReferences(ref.current)
      )
      if (isInView(ref.current)) {
        setViewed(true)
      }
    }
  }, [])

  useEffect(() => {
    if (!viewed && ref.current !== null && isInView(ref.current)) {
      setViewed(true)
    }
  }, [viewed, scroll, references])

  function handleOnClick (): void {
    if (references === undefined) return
    dispatch(setMouseabilityReference(references))
    setViewed(true)
    setTrigger(prev => prev + 1)

    if (typeof response.references === 'number') {
      if (response.references === RESPONSE_NOTFOUND) {
        return setIsRemoved(true)
      }
    } else if (Array.isArray(response.references) && response.references.length > 0) {
      updateHistoryState({ pageData: { references } })
      const [reference] = response.references as any[]
      const [key] = Object.keys(reference.nodes)
      setIsArchived(reference.nodes[key].isArchived)
    }

    dispatch(pageToggleRight(true))
  }

  if (isRemoved) {
    return (
      <TealToolTip title='This legislation has been removed from our service and is no longer available.'>
        <span className='fc-legislation'>
          {children}
        </span>
      </TealToolTip>
    )
  }

  return (
    <SpanButton
      ref={ref}
      onClick={handleOnClick}
      className='fc-legislation'
      data-event-category='Q&A'
      data-event-action='Legislation Reference'
      data-event-label={references}
    >
      {children}
      {isArchived && (
        <span>(Archived)</span>
      )}
    </SpanButton>
  )
}
